import MediaError from './MediaError';
import { mediaTypes } from '../../constants/mediaTypes';
import cameraOffIconUrl from '../../components/VideoPlayer/ControlBlock/assets/cameraOff.svg';
import {
  cameraOffMarkerSides,
  cameraOffMarkers,
  cameraOffMarkerSize,
  cameraOffPlaceholderSize,
} from '../../constants/cameraOffMarkers';

function createCameraOffImage() {
  const image = new Image();

  image.src = cameraOffIconUrl;

  return image;
}

function createSilentAudioTrack() {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;

  if (!AudioContextClass) {
    return null;
  }

  const audioContext = new AudioContextClass();
  const destination = audioContext.createMediaStreamDestination();
  const oscillator = audioContext.createOscillator();
  const gain = audioContext.createGain();

  gain.gain.value = 0;

  oscillator.connect(gain);
  gain.connect(destination);
  oscillator.start();

  const [track] = destination.stream.getAudioTracks();

  return {
    track,
    cleanup: () => {
      if (oscillator?.stop) {
        try {
          oscillator.stop();
        } catch (error) {
          // oscillator may already be stopped
        }
      }

      if (track && track.readyState !== 'ended') {
        track.stop();
      }

      if (audioContext?.close) {
        audioContext.close();
      }
    },
  };
}

function createCameraOffVideoTrack() {
  const canvas = document.createElement('canvas');

  canvas.width = cameraOffPlaceholderSize.width;
  canvas.height = cameraOffPlaceholderSize.height;

  const context = canvas.getContext('2d');

  if (!context || !canvas.captureStream) {
    return null;
  }

  const image = createCameraOffImage();

  const drawMarkers = () => {
    cameraOffMarkers.forEach(marker => {
      const x =
        marker.xSide === cameraOffMarkerSides.right ? canvas.width - cameraOffMarkerSize.width : 0;
      const y =
        marker.ySide === cameraOffMarkerSides.bottom
          ? canvas.height - cameraOffMarkerSize.height
          : 0;

      context.fillStyle = marker.color;
      context.fillRect(x, y, cameraOffMarkerSize.width, cameraOffMarkerSize.height);
    });
  };

  const drawPlaceholder = () => {
    context.fillStyle = '#000000';
    context.fillRect(0, 0, canvas.width, canvas.height);

    if (image.complete) {
      const iconSize = 220;
      const x = (canvas.width - iconSize) / 2;
      const y = (canvas.height - iconSize) / 2;

      context.drawImage(image, x, y, iconSize, iconSize);
    }

    drawMarkers();
  };

  image.onload = drawPlaceholder;

  drawPlaceholder();

  const intervalId = window.setInterval(drawPlaceholder, 1000);
  const stream = canvas.captureStream(5);
  const [track] = stream.getVideoTracks();

  if (!track) {
    window.clearInterval(intervalId);
    return null;
  }

  track.contentHint = 'detail';

  return {
    track,
    cleanup: () => {
      window.clearInterval(intervalId);

      if (track.readyState !== 'ended') {
        track.stop();
      }
    },
  };
}

class Media {
  constructor() {
    this.videoResolutions = {
      qvga: { width: { ideal: 320 }, height: { ideal: 240 } },
      vga: { width: { ideal: 640 }, height: { ideal: 480 } },
      hd: { width: { ideal: 1280 }, height: { ideal: 720 } },
    };
    this.constraints = {
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: {
        facingMode: 'user',
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 24, max: 30 },
      },
    };
    this.videoEncodings = [
      { scaleResolutionDownBy: 4, maxBitrate: 300000 },
      { scaleResolutionDownBy: 2, maxBitrate: 900000 },
      { scaleResolutionDownBy: 1, maxBitrate: 2500000 },
    ];
    this.screenshareEncodings = [
      { dtx: true, maxBitrate: 1500000 },
      { dtx: true, maxBitrate: 6000000 },
    ];
    this.videoCodecOptions = {
      videoGoogleStartBitrate: 1200,
      videoGoogleMaxBitrate: 2500,
      videoGoogleMinBitrate: 300,
    };
    this._streams = {
      [mediaTypes.audioType]: null,
      [mediaTypes.videoType]: null,
      [mediaTypes.screenType]: null,
    };
    this.audioInputDevides = new Map();
    this.audioOutputDevices = new Map();
    this.videoInputDevices = new Map();
    this._areDevidesEnumerated = false;
    this.deviceKinds = {
      audioInput: 'audioinput',
      audioOutput: 'audiooutput',
      videoInput: 'videoinput',
    };
  }

  async getLocalMedia(mediaType, deviceId, isKeepingPreviousState) {
    if (isKeepingPreviousState) {
      return this._streams[mediaType];
    }
    try {
      switch (mediaType) {
        case mediaTypes.videoType:
        case mediaTypes.audioType: {
          const constraints = {
            audio: mediaType === mediaTypes.audioType && { ...this.constraints.audio, deviceId },
            video: mediaType === mediaTypes.videoType && { ...this.constraints.video, deviceId },
          };
          const stream = await navigator.mediaDevices.getUserMedia(constraints);

          stream.getAudioTracks().forEach(track => {
            track.contentHint = 'speech';
          });

          stream.getVideoTracks().forEach(track => {
            track.contentHint = 'motion';
          });

          this._streams[mediaType] = stream;
          return stream;
        }
        case mediaTypes.screenType: {
          const stream = await navigator.mediaDevices.getDisplayMedia();
          this._streams[mediaType] = stream;
          return stream;
        }
        default:
          throw new MediaError(null, mediaType);
      }
    } catch (e) {
      throw new MediaError(e.name, mediaType);
    }
  }

  removeLocalMedia(mediaType) {
    if (
      mediaType === mediaTypes.audioType ||
      mediaType === mediaTypes.videoType ||
      mediaType === mediaTypes.screenType
    ) {
      this._streams[mediaType] = null;
    }
  }

  async _enumerateDevices() {
    await navigator.mediaDevices
      .enumerateDevices()
      .then(devices => {
        devices.forEach(device => {
          const { deviceId, kind } = device;
          switch (kind) {
            case this.deviceKinds.audioInput:
              this.audioInputDevides.set(deviceId, device);
              break;
            case this.deviceKinds.audioOutput:
              this.audioOutputDevices.set(deviceId, device);
              break;
            case this.deviceKinds.videoInput:
              this.videoInputDevices.set(deviceId, device);
              break;
            // no default
          }
        });
        this._areDevidesEnumerated = true;
      })
      .catch(e => {
        // eslint-disable-next-line no-console
        console.error('_enumerateDevices', { e });
      });
  }
}

export { createSilentAudioTrack, createCameraOffVideoTrack };
export default Media;
